import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import DashboardBrokerTop from './DashboardBrokerTop';
import DashboardTable from '../DashboardTable';
import AddBrokerPopup from './AddBrokerPopup';
import EditBrokerPopup from './EditBrokerPopup';
import useRequest from '../../../hooks/useRequest';
import { useNavigate } from 'react-router-dom';

function DashboardBroker() {
  const [brokers, setBrokers] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isActive, setIsActive] = useState(null);
  const [isActivated, setIsActivated] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [error, setError] = useState(null);
  const { sendRequest, loading } = useRequest();
  const navigate = useNavigate();

  const fetchBrokers = useCallback(async () => {
    try {
      setError(null);
      let url = '/api/broker/';
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (isActive !== null) params.append('is_active', isActive);
      if (isActivated !== null) params.append('is_activated', isActivated);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await sendRequest('GET', url);
      setBrokers(response || []);
    } catch (error) {
      console.error('Failed to fetch brokers:', error);
      setError(error.message || 'Failed to fetch brokers');
    }
  }, [sendRequest, searchQuery, isActive, isActivated]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    fetchBrokers();
  }, [fetchBrokers]);

  const handleDelete = async (brokerId) => {
    if (!window.confirm('Are you sure you want to delete this broker?')) {
      return;
    }

    try {
      await sendRequest('DELETE', `/api/broker/?id=${brokerId}`);
      fetchBrokers();
    } catch (error) {
      console.error('Failed to delete broker:', error);
      setError(error.message || 'Failed to delete broker');
    }
  };

  const handleEdit = (broker) => {
    setSelectedBroker(broker);
    setIsEditPopupOpen(true);
  };

  const handleViewGroup = (brokerId) => {
    navigate(`/dashboard/group/${brokerId}`);
  };

  const columnNames = ['ID', 'Facebook ID', 'Facebook Link', 'Status', 'Actions'];
  
  const handleStatusClear = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsActive(null);
      setIsActivated(null);
      setIsTransitioning(false);
    }, 300);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleAddBroker = () => {
    setIsPopupOpen(true);
  };

  const handleBrokerCreated = () => {
    fetchBrokers();
  };

  const handleBrokerUpdated = () => {
    fetchBrokers();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        variants={itemVariants}
        className="mb-6"
      >
        <DashboardBrokerTop 
          totalBrokers={brokers.length}
          onSearch={handleSearch}
          onStatusClear={handleStatusClear}
          onAddBroker={handleAddBroker}
        />
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
      >
        <AnimatePresence mode="wait">
          {error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center p-8 text-center"
            >
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl text-red-600">⚠️</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Brokers</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchBrokers}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </motion.div>
          ) : loading || isTransitioning ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center justify-center p-8"
            >
              <div className="relative">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm text-blue-600 font-medium">Loading...</span>
                </div>
              </div>
            </motion.div>
          ) : brokers.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center p-8 text-center"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl text-blue-600">😔</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Brokers Found</h3>
              <p className="text-gray-500">
                {searchQuery 
                  ? "No brokers match your search criteria"
                  : "There are no brokers in the system yet"}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <DashboardTable 
                columnNames={columnNames} 
                data={brokers.map(broker => ({
                  id: broker.id,
                  fb_id: broker.fb_id || '-',
                  fb_link: broker.fb_link || '-',
                  status: (
                    <div className="flex items-center space-x-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        broker.is_active ? 'bg-green-500' : 'bg-red-500'
                      }`}></span>
                      <span>{broker.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  ),
                  actions: (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewGroup(broker.id)}
                        className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
                        title="View Group"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(broker)}
                        className="p-2 text-gray-600 hover:text-gray-700 transition-colors"
                        title="Edit"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(broker.id)}
                        className="p-2 text-red-600 hover:text-red-700 transition-colors"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )
                }))}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AddBrokerPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onSuccess={handleBrokerCreated}
      />

      <EditBrokerPopup
        isOpen={isEditPopupOpen}
        onClose={() => setIsEditPopupOpen(false)}
        onSuccess={handleBrokerUpdated}
        broker={selectedBroker}
      />
    </motion.div>
  );
}

export default DashboardBroker; 